<template>
  <v-row class="d-flex justify-end">
    <v-col
      cols="12"
      md="6"
      class="d-flex align-center"
      v-if="!hideGoBack && mode !== 'order' && !options.hidePrevButton"
    >
      <LinkButton
        v-if="
          !$vuetify.breakpoint.xs &&
            options.prevButton &&
            options.prevButton.type === 'link-button'
        "
        :class="options.prevButton.class"
        :options="options.prevButton.options"
        :label="options.prevButton.label"
        @click="goBack"
      />

      <v-btn
        v-else-if="options.back && options.back.show !== false"
        v-bind="backButtonProps"
        @click="goBack"
        :disabled="isLoading"
      >
        {{ options.back && options.back.label }}
      </v-btn>
    </v-col>

    <v-col cols="12" :md="hideGoBack ? 12 : 6" class="d-flex justify-end">
      <v-btn
        v-bind="confirmButtonProps"
        @click="buy"
        :class="confirmClass"
        :disabled="isPaymentDisabled || portInfoFormNotCompleted"
      >
        {{ options.confirm.label || $t("payment.paymentTypeList.proceed") }}
      </v-btn>
    </v-col>
  </v-row>
</template>
<script>
import OrderService from "~/service/orderService";
import PaymentTypeService from "~/service/paymentTypeService";
import { payment } from "~/components/payment/gateways/gatewayUtilsService";

export default {
  name: "PaymentButtons",

  props: {
    mode: { type: String, required: true },
    orderId: { type: [String, Number], default: null },
    options: { type: Object, default: () => ({}) },
    backRoute: { type: [String, Object], default: "/checkout" },
    hideGoBack: { type: Boolean, default: false },
    isPaymentDisabled: { type: Boolean, default: false },
    portInfoFormNotCompleted: { type: Boolean, required: false, default: false }

    // ← paymentType prop REMOVED – we ONLY use Vuex now
  },

  computed: {
    // === EVERYTHING FROM YOUR PAYMENT STORE ===
    isLoading() {
      return this.$store.state.payment.isLoading;
    },
    selectedPaymentAuthId() {
      return this.$store.state.payment.selectedPaymentAuthId;
    },
    rememberNewCard() {
      return this.$store.state.payment.rememberNewCard;
    },
    paymentAdditionalData() {
      return this.$store.state.payment.paymentAdditionalData;
    },
    selectedPaymentType() {
      return this.$store.state.payment.selectedPaymentType; // ← THIS IS WHAT YOU WANTED
    },
    selectedGatewayClient() {
      return this.$store.state.payment.selectedGatewayClient;
    },

    // Template helpers (unchanged)
    backButtonProps() {
      return {
        color: this.options.back?.color,
        outlined: this.options.back?.outline,
        icon: this.options.back?.icon,
        link: this.options.back?.link,
        depressed: true,
        large: this.options.back?.large,
        class: this.options.back?.class
      };
    },
    confirmButtonProps() {
      return {
        color: this.options.confirm?.color,
        outlined: this.options.confirm?.outline,
        depressed: this.options.confirm?.depressed,
        large: this.options.confirm?.large,
        class: this.options.confirm?.class,
        loading: this.isLoading,
        disabled: this.isLoading
      };
    },
    confirmClass() {
      return this.options.confirm?.class || "gateway-btn mt-3 mt-sm-0 ml-2";
    }
  },

  methods: {
    // ====================== UNIVERSAL BUY() – NOW 100% STORE-BASED ======================
    async buy() {
      // HARD SAFETY – exactly what you asked for
      if (
        !this.selectedPaymentType ||
        !this.selectedPaymentType.paymentTypeId
      ) {
        console.error(
          "🚨 PaymentButtons: selectedPaymentType is NULL in Vuex! Call setSelectedPaymentType in your parent BEFORE rendering this component."
        );
        this.$store.dispatch(
          "payment/setResponseMessage",
          new Error("Missing selectedPaymentType in store")
        );
        return;
      }

      this.$store.dispatch("payment/setIsLoading", true);
      this.$store.dispatch("payment/setResponseMessage", null);

      try {
        const paymentTypeId = this.selectedPaymentType.paymentTypeId; // ← FROM STORE
        let userPaymentAuthId = this.selectedPaymentAuthId;
        let paymentData = { ...this.paymentAdditionalData };
        paymentData.remember_card = this.rememberNewCard;

        let result = null;

        if (this.mode === "addAuth") {
          paymentData = { remember_card: true };
          result = await PaymentTypeService.addAuth(paymentTypeId, paymentData);
        } else if (this.mode === "cart") {
          const payload = { paymentTypeId, paymentData };
          if (this.selectedGatewayClient === "payment-type-monetaweb-paypal") {
            payload.paymentData.paypal = true;
          }
          if (userPaymentAuthId != null)
            payload.userPaymenthAuthId = userPaymentAuthId;
          result = await this.$store.dispatch("cart/buyCart", payload);
        } else if (this.mode === "order") {
          const authId = userPaymentAuthId != null ? userPaymentAuthId : null;
          if (this.selectedGatewayClient === "payment-type-monetaweb-paypal") {
            result = await OrderService.modifyPaypalOrderPaymentType(
              this.orderId,
              paymentTypeId,
              authId,
              paymentData
            );
          } else {
            result = await OrderService.modifyOrderPaymentType(
              this.orderId,
              paymentTypeId,
              authId,
              paymentData
            );
          }
        }

        if (result) {
          let oid = null;
          if (this.mode === "cart")
            oid = result.order?.orderId || result.orderId;
          else if (this.mode === "order" || this.mode === "addAuth")
            oid = this.orderId;

          if (oid) {
            await this.$store.dispatch("app/updateCurrentOrderId", oid, {
              root: true
            });
          }
          this.handlePayResponseOk(result);
        } else {
          const errorRoute =
            this.mode === "cart" ? "/payment-error" : "/cart/payment-error";
          this.$router.push(errorRoute);
        }
      } catch (err) {
        this.$store.dispatch("payment/setResponseMessage", err);
      } finally {
        this.$store.dispatch("payment/setIsLoading", false);
      }
    },

    handlePayResponseOk(cart) {
      global.EventBus.$emit("pay-response-ok", cart);
      if (cart?.paymentUrl) {
        const orderId = cart.order?.orderId || cart.orderId;
        this.doRedirect(
          cart.paymentUrl,
          cart.paymentParameters,
          "get",
          orderId
        );
      } else {
        const orderId = cart.order?.orderId || cart.orderId || this.orderId;
        this.$router.push("/cart/payment-completed/" + orderId);
      }
    },

    doRedirect(paymentUrl, paymentParameters, method, orderId) {
      payment(paymentUrl, paymentParameters, method, orderId);
    }
  },

  mounted() {
    this.$store.dispatch("payment/setIsLoading", false);
  }
};
</script>
